import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type {
  DceAnalysis,
  DceRequiredDocument,
  OpportunityConstraints,
} from '../../types/dce-analysis.types';
import { ConfidenceBadge } from './ConfidenceBadge';
import { SourceLink } from './SourceLink';

interface Props {
  analysis: DceAnalysis;
  requiredDocuments: DceRequiredDocument[];
}

const FORMAT_LABELS: Record<string, string> = {
  papier: 'Papier',
  electronique: 'Électronique',
  plateforme_dematerialisee: 'Plateforme dématérialisée',
};

const PIECE_LABELS: Record<string, string> = {
  KBIS: 'K-bis',
  ATTESTATION_URSSAF: 'Attestation URSSAF',
  ATTESTATION_FISCALE: 'Attestation fiscale',
  ASSURANCE_RC_PRO: 'Assurance RC pro',
  ISO_9001: 'ISO 9001',
  ISO_14001: 'ISO 14001',
  ISO_27001: 'ISO 27001',
  DC1: 'DC1',
  DC2: 'DC2',
  DC4: 'DC4 (sous-traitance)',
  DUME: 'DUME',
  REFERENCES: 'Références',
  CV_EQUIPE: "CV de l'équipe",
  RIB: 'RIB',
  POUVOIR: 'Pouvoir',
  AUTRE: 'Autre',
};

export function ConstraintsSection({ analysis, requiredDocuments }: Props) {
  const c: OpportunityConstraints | undefined = analysis.opportunityConstraints;
  const mandatory = requiredDocuments.filter((d) => d.mandatory);
  const optional = requiredDocuments.filter((d) => !d.mandatory);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Contraintes administratives et calendaires</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Bloc scalaire */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Field label="Lieu d'exécution" value={c?.executionLocation} />
          <Field label="Langue exigée" value={c?.requiredLanguage ?? 'Français (implicite)'} />
          <Field
            label="Format de remise"
            value={c?.submissionFormat ? FORMAT_LABELS[c.submissionFormat] ?? c.submissionFormat : null}
          />
          <Field label="Plateforme" value={c?.submissionPlatform} />
          <Field
            label="Visite de site obligatoire"
            value={
              c?.mandatorySiteVisit === null || c?.mandatorySiteVisit === undefined
                ? null
                : c.mandatorySiteVisit
                  ? `Oui${c.siteVisitDate ? ` — ${formatDate(c.siteVisitDate)}` : ''}`
                  : 'Non'
            }
          />
        </div>

        {/* Pièces administratives requises */}
        <div className="border-t pt-3">
          <h4 className="text-sm font-semibold mb-2">
            Pièces administratives requises ({requiredDocuments.length})
          </h4>
          {requiredDocuments.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Aucune pièce administrative spécifique identifiée.
            </p>
          )}
          {mandatory.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="text-xs font-medium text-muted-foreground">
                Obligatoires ({mandatory.length})
              </div>
              {mandatory.map((d) => (
                <PieceItem key={d.id} doc={d} />
              ))}
            </div>
          )}
          {optional.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">
                Souhaitées ({optional.length})
              </div>
              {optional.map((d) => (
                <PieceItem key={d.id} doc={d} />
              ))}
            </div>
          )}
        </div>

        {/* Conditions juridiques + sécurité (Json libre) */}
        {hasContent(c?.legalConditions) && (
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold mb-2">Conditions juridiques</h4>
            <JsonView data={c!.legalConditions} />
          </div>
        )}
        {hasContent(c?.securityConstraints) && (
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold mb-2">Contraintes sécurité / RGPD</h4>
            <JsonView data={c!.securityConstraints} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PieceItem({ doc }: { doc: DceRequiredDocument }) {
  const label = PIECE_LABELS[doc.pieceType] ?? doc.pieceType;
  return (
    <div className="rounded border bg-card p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="font-medium">
            {label}
          </Badge>
          {doc.mandatory && (
            <span className="text-[10px] text-red-600 font-semibold uppercase">
              Obligatoire
            </span>
          )}
          {doc.notes && (
            <span className="text-xs text-muted-foreground italic">— {doc.notes}</span>
          )}
        </div>
        <ConfidenceBadge confidence={doc.confidence} />
      </div>
      {doc.sourceExcerpt && (
        <SourceLink
          documentId={doc.sourceDocumentId}
          filename={doc.sourceDocumentFilename}
          pageNumber={doc.sourcePageNumber}
          excerpt={doc.sourceExcerpt}
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value || <span className="text-muted-foreground italic">Non précisé</span>}</div>
    </div>
  );
}

function JsonView({ data }: { data: Record<string, unknown> }) {
  return (
    <ul className="text-xs space-y-1">
      {Object.entries(data).map(([key, value]) => (
        <li key={key}>
          <span className="font-medium">{key}</span>{' '}
          <span className="text-muted-foreground">{stringifyValue(value)}</span>
        </li>
      ))}
    </ul>
  );
}

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return JSON.stringify(v);
}

function hasContent(obj: unknown): obj is Record<string, unknown> {
  return !!obj && typeof obj === 'object' && Object.keys(obj as Record<string, unknown>).length > 0;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
